import { appAlert } from '@/lib/appAlert';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  AppState,
  BackHandler,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { CHAT_AQUA, contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { fetchProfilesByIds } from '@/lib/agreements';
import {
  DELETED_SENDER_ID, fetchConnectionMessageReads, fetchConnectionMessages, markConnectionMessagesRead,
  sendConnectionMessage, type ConnectionMessage,
} from '@/lib/connectionMessages';
import type { Profile } from '@/lib/database.types';
import { chatDisplayName } from '@/lib/format';
import { autoTransliterateOnWordBoundary } from '@/lib/greeklish';
import { getErrorMessage } from '@/lib/functions';
import { playPop, playTap } from '@/lib/sounds';
import { blockUser, fetchMyBlockRelations, unblockUser } from '@/lib/chatModeration';
import { ErrorText } from '@/components/ErrorText';

export default function ConnectionMessagesScreen() {
  const { id, otherId, name: nameParam } = useLocalSearchParams<{ id: string; otherId?: string; name?: string }>();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { colors } = useTheme();
  const { user } = useAuth();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<ConnectionMessage[]>([]);
  const [otherProfile, setOtherProfile] = useState<Profile | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reads, setReads] = useState<Record<string, string>>({});
  const [blockRelations, setBlockRelations] = useState<{ blocker_id: string; blocked_id: string }[]>([]);
  const [blockBusy, setBlockBusy] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const [reconnectTick, setReconnectTick] = useState(0);
  const listRef = useRef<FlatList<ConnectionMessage>>(null);

  const otherName = otherProfile?.full_name?.trim() || (nameParam as string) || t('common.user');

  const blockedByMeId = useMemo(
    () => blockRelations.find((r) => r.blocker_id === user?.id && r.blocked_id === otherId)?.blocked_id ?? null,
    [blockRelations, otherId, user?.id],
  );
  const blockedMeId = useMemo(
    () => blockRelations.find((r) => r.blocked_id === user?.id && r.blocker_id === otherId)?.blocker_id ?? null,
    [blockRelations, otherId, user?.id],
  );
  const isBlocked = !!blockedByMeId || !!blockedMeId;

  async function refreshBlockStatus() {
    try {
      const relations = await fetchMyBlockRelations();
      setBlockRelations(relations);
    } catch { /* μη κρίσιμο */ }
  }

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [msgs, readRows] = await Promise.all([
        fetchConnectionMessages(id), fetchConnectionMessageReads(id).catch(() => ({})),
      ]);
      setMessages(msgs);
      setReads(readRows);
      if (otherId && !otherProfile) {
        const map = await fetchProfilesByIds([otherId]);
        setOtherProfile(map[otherId] ?? null);
      }
      await AsyncStorage.setItem(`connection_messages_seen_${id}`, new Date().toISOString());
      void markConnectionMessagesRead(id).catch(() => {});
      void refreshBlockStatus();
    } catch {
      setError(t('agreementDetail.messagesLoadError'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        void load();
        setReconnectTick((v) => v + 1);
      }
    });
    return () => sub.remove();
  }, [load]);

  useFocusEffect(
    useCallback(() => {
      void load();
      if (!id) return;
      const channel = supabase
        .channel(`connection-messages-${id}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'connection_messages',
          filter: `connection_id=eq.${id}`,
        }, (payload) => {
          const raw = payload.new as { id: string; connection_id: string; sender_id: string | null; body: string; created_at: string };
          const row: ConnectionMessage = { ...raw, sender_id: raw.sender_id ?? DELETED_SENDER_ID };
          setMessages((prev) => (prev.some((m) => m.id === row.id) ? prev : [...prev, row]));
          if (row.sender_id !== user?.id) playPop();
          requestAnimationFrame(() => listRef.current?.scrollToEnd({ animated: true }));
          void AsyncStorage.setItem(`connection_messages_seen_${id}`, new Date().toISOString());
          void markConnectionMessagesRead(id).catch(() => {});
        })
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'connection_message_reads',
          filter: `connection_id=eq.${id}`,
        }, (payload) => {
          const row = payload.new as { user_id: string; last_read_at: string } | undefined;
          if (!row) return;
          setReads((prev) => ({ ...prev, [row.user_id]: row.last_read_at }));
        })
        .subscribe();
      channelRef.current = channel;
      return () => {
        void supabase.removeChannel(channel);
        channelRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, load, reconnectTick]),
  );

  function onChangeDraft(rawText: string) {
    setDraft(autoTransliterateOnWordBoundary(rawText));
  }

  async function onSend() {
    const body = draft.trim();
    if (!body || !id || !user || sending || isBlocked) return;
    setSending(true);
    setError(null);
    setDraft('');
    try {
      const sent = await sendConnectionMessage(id, user.id, body);
      setMessages((prev) => (prev.some((m) => m.id === sent.id) ? prev : [...prev, sent]));
      playTap();
      requestAnimationFrame(() => listRef.current?.scrollToEnd({ animated: true }));
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.messagesSendError')));
      setDraft(body);
    } finally {
      setSending(false);
    }
  }

  function goBack() {
    if (router.canGoBack()) { router.back(); return; }
    if (otherId) { router.replace({ pathname: '/(app)/users/[id]', params: { id: otherId } } as never); return; }
    router.replace('/(app)/circle' as never);
  }

  useEffect(() => {
    if (Platform.OS !== 'android') return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      goBack();
      return true;
    });
    return () => sub.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function onBlock() {
    if (!otherId) return;
    appAlert(
      t('agreementDetail.blockConfirmTitle', { name: otherName }),
      t('agreementDetail.circleChatBlockConfirmBody'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('agreementDetail.blockAction'),
          style: 'destructive',
          onPress: async () => {
            setBlockBusy(true);
            try {
              await blockUser(otherId);
              await refreshBlockStatus();
            } catch (e) {
              appAlert(t('common.error'), getErrorMessage(e, t('agreementDetail.blockError')));
            } finally {
              setBlockBusy(false);
            }
          },
        },
      ],
    );
  }

  async function onUnblock() {
    if (!blockedByMeId) return;
    setBlockBusy(true);
    try {
      await unblockUser(blockedByMeId);
      await refreshBlockStatus();
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('agreementDetail.blockError')));
    } finally {
      setBlockBusy(false);
    }
  }

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? insets.top : 0}
    >
      <View style={[styles.header, { paddingTop: insets.top + spacing.sm }]}>
        <Pressable onPress={goBack} hitSlop={12} accessibilityRole="button">
          <Ionicons name="chevron-back" size={24} color={colors.text} />
        </Pressable>
        <Text style={styles.headerTitle} numberOfLines={1}>{otherName}</Text>
        {otherId ? (
          <Pressable onPress={onBlock} hitSlop={12} accessibilityRole="button" accessibilityLabel={t('agreementDetail.blockUserAction', { name: otherName })}>
            <Ionicons name="ellipsis-vertical" size={20} color={colors.text} />
          </Pressable>
        ) : <View style={{ width: 20 }} />}
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={CHAT_AQUA} />
        </View>
      ) : (
        <FlatList
          ref={listRef}
          data={messages}
          keyExtractor={(m) => m.id}
          contentContainerStyle={styles.listContent}
          onContentSizeChange={() => listRef.current?.scrollToEnd({ animated: false })}
          ListEmptyComponent={
            <View style={styles.center}>
              <Ionicons name="chatbubbles-outline" size={40} color={colors.border} />
              <Text style={styles.emptyText}>{t('agreementDetail.circleChatEmpty', { name: otherName })}</Text>
            </View>
          }
          renderItem={({ item }) => {
            const mine = item.sender_id === user?.id;
            const senderName = mine ? t('agreementDetail.messagesYou') : chatDisplayName(otherProfile?.full_name ?? null, null) || otherName;
            const isRead = mine && !!otherId && !!reads[otherId] && new Date(reads[otherId]) >= new Date(item.created_at);
            return (
              <View style={[styles.bubbleRow, mine && styles.bubbleRowMine]}>
                <View style={styles.bubbleColumn}>
                  <View style={[styles.bubble, mine ? styles.bubbleMine : styles.bubbleOther]}>
                    <Text style={[styles.bubbleSender, mine && styles.bubbleSenderMine]}>{senderName}</Text>
                    <Text style={[styles.bubbleText, mine && styles.bubbleTextMine]}>{item.body}</Text>
                    <View style={styles.bubbleMetaRow}>
                      <Text style={[styles.bubbleTime, mine && styles.bubbleTimeMine]}>
                        {new Date(item.created_at).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                      </Text>
                      {mine && (
                        isRead
                          ? <Text style={styles.bubbleReadEmoji}>👀✔️</Text>
                          : <Ionicons name="checkmark" size={14} color="rgba(255,255,255,0.6)" style={styles.bubbleReadIcon} />
                      )}
                    </View>
                  </View>
                </View>
              </View>
            );
          }}
        />
      )}

      <ErrorText>{error}</ErrorText>

      {isBlocked ? (
        <View style={[styles.closedNotice, { paddingBottom: insets.bottom + spacing.sm, flexDirection: 'column', gap: spacing.xs }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Ionicons name="ban-outline" size={16} color={colors.textMuted} />
            <Text style={styles.closedNoticeText}>
              {blockedByMeId
                ? t('agreementDetail.circleChatBlockedByMeNotice', { name: otherName })
                : t('agreementDetail.circleChatBlockedMeNotice', { name: otherName })}
            </Text>
          </View>
          {!!blockedByMeId && (
            <Pressable onPress={onUnblock} disabled={blockBusy} accessibilityRole="button">
              <Text style={styles.unblockLink}>
                {blockBusy ? t('common.loading') : t('agreementDetail.unblockAction')}
              </Text>
            </Pressable>
          )}
        </View>
      ) : (
        <View style={[styles.inputRow, { paddingBottom: insets.bottom + spacing.sm }]}>
          <TextInput
            value={draft}
            onChangeText={onChangeDraft}
            placeholder={t('agreementDetail.circleChatPlaceholder')}
            placeholderTextColor={colors.textMuted}
            style={styles.input}
            multiline
            maxLength={4000}
          />
          <Pressable
            style={[styles.sendBtn, (!draft.trim() || sending) && styles.sendBtnDisabled]}
            onPress={onSend}
            disabled={!draft.trim() || sending}
            accessibilityRole="button"
          >
            <Ionicons name="send" size={18} color="#fff" />
          </Pressable>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerTitle: { flex: 1, textAlign: 'center', color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: spacing.sm, paddingTop: spacing.xl * 2 },
    emptyText: { color: colors.textMuted, fontSize: fontSize.sm, textAlign: 'center', paddingHorizontal: spacing.xl },
    listContent: {
      // Σε πλατιά οθόνη τα μηνύματα μένουν σε στήλη αναγνώσιμου πλάτους
      // αντί να απλώνονται από άκρη σε άκρη.
      width: '100%',
      maxWidth: contentMaxWidth.wide,
      alignSelf: 'center',
      padding: spacing.md,
      gap: spacing.xs,
      flexGrow: 1,
    },
    bubbleRow: { flexDirection: 'row', justifyContent: 'flex-start' },
    bubbleRowMine: { justifyContent: 'flex-end' },
    bubbleColumn: { maxWidth: '80%' },
    bubble: {
      borderRadius: radius.lg,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      marginVertical: 2,
    },
    bubbleOther: { backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border },
    bubbleMine: { backgroundColor: CHAT_AQUA },
    bubbleSender: { color: CHAT_AQUA, fontSize: 11, fontWeight: '700', marginBottom: 2 },
    bubbleSenderMine: { color: 'rgba(255,255,255,0.85)' },
    bubbleText: { color: colors.text, fontSize: fontSize.sm, lineHeight: 19 },
    bubbleTextMine: { color: '#fff' },
    bubbleTime: { color: colors.textMuted, fontSize: 10, marginTop: 2, alignSelf: 'flex-end' },
    bubbleTimeMine: { color: 'rgba(255,255,255,0.75)' },
    bubbleMetaRow: { flexDirection: 'row', alignItems: 'center', gap: 3, alignSelf: 'flex-end', marginTop: 2 },
    bubbleReadIcon: { marginLeft: 1 },
    bubbleReadEmoji: { fontSize: 11, marginLeft: 1 },
    closedNotice: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    closedNoticeText: { color: colors.textMuted, fontSize: fontSize.sm, textAlign: 'center' },
    inputRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: spacing.sm,
      paddingHorizontal: spacing.md,
      paddingTop: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    input: {
      flex: 1,
      maxHeight: 100,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      color: colors.text,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      fontSize: fontSize.sm,
    },
    sendBtn: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: CHAT_AQUA,
      alignItems: 'center',
      justifyContent: 'center',
    },
    sendBtnDisabled: { opacity: 0.5 },
    unblockLink: { color: CHAT_AQUA, fontSize: fontSize.sm, fontWeight: '700' },
  });
}
